import { ReactNode, useContext, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faBars,
  faBell,
  faClock,
  faGaugeHigh,
  faHeartPulse,
  faHeartbeat,
  faListUl,
  faRightFromBracket,
  faTv,
  faUsers,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import Icon3D from "../ui/icon-3d";
import { Tone } from "../ui/tones";
import PlatformSwitcher from "./platform-switcher";
import ThemeToggle from "../theme-toggle";
import { AuthContext } from "../../providers/AuthProvider";
import { fetchAlerts } from "../../utils/api";
import useAutoRefresh from "../../hooks/use-auto-refresh";

type NavItem = { to: string; label: string; icon: IconDefinition; tone: Tone; end?: boolean; badge?: boolean; admin?: boolean };

const NAV: NavItem[] = [
  { to: "/", label: "Overview", icon: faGaugeHigh, tone: "indigo", end: true },
  { to: "/alerts", label: "Alerts", icon: faBell, tone: "rose", badge: true },
  { to: "/logs", label: "API logs", icon: faListUl, tone: "sky" },
  { to: "/cron-jobs", label: "Cron jobs", icon: faClock, tone: "violet" },
  { to: "/health", label: "Health", icon: faHeartPulse, tone: "emerald" },
  { to: "/users", label: "Users", icon: faUsers, tone: "slate", admin: true },
];

function Sidebar({ openAlerts, onNavigate }: { openAlerts: number; onNavigate: () => void }) {
  const auth = useContext(AuthContext);
  const user = auth?.user;

  return (
    <aside className="glass flex h-full flex-col rounded-none border-y-0 border-l-0 lg:rounded-r-3xl">
      <div className="flex items-center gap-3 px-5 py-6">
        <Icon3D icon={faHeartbeat} tone="indigo" size="lg" />
        <div>
          <p className="font-[Gilroy-Bold] text-[19px] leading-none text-heading-color">HealthSure</p>
          <p className="mt-1 font-[Gilroy-Medium] text-[12px] text-label-muted">Sure platform monitor</p>
        </div>
      </div>

      <nav aria-label="Main" className="flex-1 space-y-1 px-3">
        {NAV.filter((item) => !item.admin || user?.type !== "agent").map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              clsx(
                "group flex items-center gap-3 rounded-2xl px-3 py-2.5 font-[Gilroy-Medium] text-[14px] transition-all duration-300",
                isActive ? "bg-active-background text-heading-color shadow-[inset_3px_0_0_#6366f1]" : "text-light-gray hover:bg-overlay hover:text-heading-color",
              )
            }
          >
            <Icon3D icon={item.icon} tone={item.tone} size="sm" flat />
            <span className="flex-1">{item.label}</span>
            {item.badge && openAlerts ? (
              <span className="rounded-full bg-rose-500 px-2 py-0.5 font-[Gilroy-Bold] text-[11px] text-white shadow-[0_4px_12px_-2px_rgba(244,63,94,0.6)]">{openAlerts}</span>
            ) : null}
          </NavLink>
        ))}
        <NavLink to="/wall" onClick={onNavigate} className="mt-4 flex items-center gap-3 rounded-2xl border border-dashed border-edge-strong px-3 py-2.5 font-[Gilroy-Medium] text-[14px] text-light-gray transition-colors hover:border-accent hover:text-heading-color">
          <Icon3D icon={faTv} tone="orange" size="sm" flat />
          Status wall (TV)
        </NavLink>
      </nav>

      <div className="m-3 flex items-center gap-3 rounded-2xl border border-edge bg-overlay p-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 to-violet-600 font-[Gilroy-Bold] text-[14px] text-white">
          {user?.name?.charAt(0) ?? "U"}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-[Gilroy-Medium] text-[13px] text-heading-color">{user?.name}</p>
          <p className="truncate font-[Gilroy-Medium] text-[11px] capitalize text-label-muted">{user?.type?.replace("-", " ")}</p>
        </div>
        <button
          type="button"
          onClick={auth?.HandleUserLogOut}
          aria-label="Log out"
          title="Log out"
          className="flex h-8 w-8 items-center justify-center rounded-xl text-light-gray transition-colors hover:bg-overlay-mid hover:text-heading-color"
        >
          <FontAwesomeIcon icon={faRightFromBracket} className="text-sm" />
        </button>
      </div>
    </aside>
  );
}

export default function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openAlerts, setOpenAlerts] = useState(0);

  const loadAlertCount = () =>
    fetchAlerts({ view: "open" })
      .then((result) => setOpenAlerts(result.counts.open))
      .catch(() => undefined);

  useEffect(() => {
    void loadAlertCount();
    setMenuOpen(false);
  }, [location.pathname]);
  useAutoRefresh(loadAlertCount, 60000);

  return (
    <div className="app-bg flex min-h-screen min-w-0">
      {menuOpen ? <button type="button" aria-label="Close navigation" className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm lg:hidden" onClick={() => setMenuOpen(false)} /> : null}
      <div className={clsx("fixed inset-y-0 left-0 z-50 w-[272px] transition-transform duration-300 ease-out lg:sticky lg:top-0 lg:h-screen lg:translate-x-0", menuOpen ? "translate-x-0" : "-translate-x-full")}>
        <Sidebar openAlerts={openAlerts} onNavigate={() => setMenuOpen(false)} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-edge bg-background/70 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <button type="button" aria-label="Open navigation" className="flex h-10 w-10 items-center justify-center rounded-xl border border-edge text-heading-color lg:hidden" onClick={() => setMenuOpen(true)}>
            <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
          </button>
          <span className="hidden items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1 font-[Gilroy-Medium] text-[12px] text-emerald-600 dark:text-emerald-300 sm:inline-flex">
            <span className="relative inline-flex h-2 w-2"><span className="pulse-ring absolute inset-0 rounded-full text-emerald-500" /><span className="relative h-2 w-2 rounded-full bg-emerald-500" /></span>
            Live
          </span>
          <div className="ml-auto flex items-center gap-2">
            <PlatformSwitcher />
            <ThemeToggle />
          </div>
        </header>
        <main className="mx-auto w-full min-w-0 max-w-[1500px] flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

// ----------------------- PAGE HEADER ------------------------------------
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="bg-gradient-heading bg-clip-text font-[Gilroy-Bold] text-[28px] leading-tight text-transparent sm:text-[32px]">{title}</h1>
        {subtitle ? <p className="mt-1 max-w-2xl font-[Gilroy-Medium] text-[14px] text-light-gray">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
